import React from 'react';
import PropTypes from 'prop-types';
// @material-ui/core
import {
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  styled,
  List,
  Tooltip,
  Typography,
  Box,
  Button,
  Grid,
  InputAdornment,
  TextField,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';

import {
  Add,
  ArrowBack,
  ChevronLeft,
  ChevronRight,
  Search,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import HorseIcon from '../../../../../components/CustomIcons/Horse';
import Horse from '../../../../../components/Horse';
import AddHorseModalComponent from '../../../Horses/components/AddHorseModal';

const drawerWidth = 225;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: { width: `calc(${theme.spacing(8)} + 1px)` },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({
    theme,
    open,
  }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const NavigationBarnComponent = ({
  navigate,
  open,
  setOpen,
}) => {
  const [search, setSearch] = React.useState('');
  const [addHorseModalOpen, setAddHorseModalOpen] = React.useState(false);

  const allBarnHorses = useSelector((state) => state.barn.allBarnHorses);
  const isFetchingBarnHorses = useSelector((state) => state.barn.isFetchingBarnHorses);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box
      component="nav"
      sx={{
        width: open
          ? drawerWidth
          : 56,
        flexShrink: { sm: 0 },
      }}
    >
      <Drawer variant="permanent" open={open}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <DrawerHeader sx={{ mt: '3rem' }}>
            <Grid container>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >

                  {open && (
                    <Box
                      sx={{
                        textAlign: 'center',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        pl: 2,
                      }}
                    >
                      <HorseIcon color="#9e9e9e" />
                      <Typography
                        variant="h5"
                        color="secondary"
                        sx={{
                          ml: 1,
                          fontWeight: 600,
                        }}
                      >Horses
                      </Typography>
                    </Box>
                  )}
                  <Tooltip
                    title={open
                      ? 'Hide Drawer'
                      : 'Show Drawer'}
                  >
                    <IconButton onClick={() => { if (open) { handleDrawerClose(); } else { handleDrawerOpen(); } }}>
                      {!open
                        ? <ChevronRight />
                        : <ChevronLeft />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              {
                  open && (
                    <Grid item xs={12}>
                      <Button
                        onClick={() => { navigate('/horses'); }}
                        startIcon={<ArrowBack />}
                      >Back to Horses Page
                      </Button>
                    </Grid>
                  )
                }
              <Grid item xs={12}>
                {
                  !open
                    ? (
                      <Tooltip title="Add new horse">
                        <IconButton
                          sx={{
                            marginBottom: 2,
                            width: '100%',
                          }}
                          size="small"
                          onClick={() => { setAddHorseModalOpen(true); }}
                          disabled={isFetchingBarnHorses}
                        >
                          <Add />
                        </IconButton>
                      </Tooltip>
                    )
                    : (
                      <Button
                        sx={{
                          marginBottom: 2,
                          width: '100%',
                        }}
                        size="small"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => { setAddHorseModalOpen(true); }}
                        disabled={isFetchingBarnHorses}
                      >
                        Add Horse
                      </Button>
                    )
              }
              </Grid>
              {
                  open && (
                    <Grid item xs={12}>
                      <TextField
                        sx={{ mb: 1 }}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="end">
                              <Search />
                            </InputAdornment>
                          ),
                        }}
                        id="search-horses-input"
                        label="Search"
                        variant="outlined"
                        onChange={(e) => { setSearch(e.target.value); }}
                      />
                    </Grid>
                  )
                }
            </Grid>
          </DrawerHeader>
          <Divider />
          <List
            sx={{
              p: 0,
              flex: 1,
              overflowY: 'auto',
            }}
          >
            {
                  allBarnHorses
                    .filter((bh) => !search || bh.horse.name.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) >= 0
                      || (!!search && !!bh.horse.showName && bh.horse.showName.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) >= 0))
                    .sort((a, b) => -b.horse.name.localeCompare(a.horse.name)).map((bh) => (
                      <ListItem key={bh.id} disablePadding sx={{ display: 'block' }} divider>
                        <Tooltip placement="right" title={bh.horse.name}>
                          <ListItemButton
                            onClick={() => { navigate(`horses/${bh.horse.id}`); }}
                            selected={window.location.pathname.indexOf(bh.horse.id) >= 0}
                            sx={{
                              minHeight: 48,
                              justifyContent: open
                                ? 'initial'
                                : 'center',
                              px: 2.5,
                            }}
                          >
                            <Horse horse={bh.horse} hideName={!open} hideShowName={!open} textWidth={140} />
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>

                    ))
                }
          </List>
        </Box>
        <AddHorseModalComponent open={addHorseModalOpen} onClose={() => { setAddHorseModalOpen(false); }} navigate={navigate} />
      </Drawer>
    </Box>
  );
}

NavigationBarnComponent.propTypes = {
  navigate: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default NavigationBarnComponent;
